/**
 * Live-session + posted-plan backend. Cloud: Supabase RPCs (server clock
 * authoritative). Local: kv on this device only (single-device semantics —
 * a posted plan still drives auto-start, a session is never shared).
 */
import { kvGet, kvSet, type PlanData } from '@coach/core';
import { LOCAL_USER } from '../../lib/app';
import { supabase } from '../../lib/supabase';

export interface SessionInfo {
  host_user_id: string;
  plan_snapshot: PlanData;
  /** epoch ms, server clock */
  started_at_ms: number;
}
export interface PostedInfo {
  plan_id: string;
  plan_name: string;
  posted_by: string | null;
  posted_by_name: string;
  posted_at_ms: number;
  /** plan revision — changes on every save of the posted plan */
  rev: string;
}
export interface SessionState {
  session: SessionInfo | null;
  posted: PostedInfo | null;
  serverNowMs: number;
}

export interface SessionBackend {
  getState(teamId: string): Promise<SessionState>;
  /** Returns the server-side started_at (already back-dated by offsetMs) and server now, both ms. */
  start(teamId: string, snapshot: PlanData, offsetMs: number): Promise<{ startedAtMs: number; serverNowMs: number }>;
  /** true when a live session was ended. */
  stop(teamId: string): Promise<boolean>;
  post(teamId: string, planId: string | null): Promise<PostedInfo | null>;
}

const ms = (s: string | null | undefined) => (s ? new Date(s).getTime() : 0);

/**
 * The posted plan expires at the team's cutoff hour (default 3 am local).
 * Applied client-side: posted_at earlier than the most recent cutoff → gone.
 */
export function expiredByCutoff(postedAtMs: number, nowMs: number, cutoffHour = 3): boolean {
  const d = new Date(nowMs);
  const cut = new Date(d.getFullYear(), d.getMonth(), d.getDate(), cutoffHour, 0, 0, 0).getTime();
  const lastCut = nowMs >= cut ? cut : cut - 86_400_000;
  return postedAtMs < lastCut;
}

class CloudBackend implements SessionBackend {
  async getState(teamId: string): Promise<SessionState> {
    const { data, error } = await supabase!.rpc('get_session_state', { t: teamId });
    if (error) throw new Error(error.message);
    const d = data as {
      session: { host_user_id: string; plan_snapshot: PlanData; started_at: string } | null;
      posted: { plan_id: string; plan_name: string; posted_by: string | null; posted_by_name: string; posted_at: string; rev: string } | null;
      server_now: string;
    };
    return {
      session: d.session ? { host_user_id: d.session.host_user_id, plan_snapshot: d.session.plan_snapshot, started_at_ms: ms(d.session.started_at) } : null,
      posted: d.posted ? { ...d.posted, posted_at_ms: ms(d.posted.posted_at) } : null,
      serverNowMs: ms(d.server_now),
    };
  }
  async start(teamId: string, snapshot: PlanData, offsetMs: number) {
    const { data, error } = await supabase!.rpc('start_session', { t: teamId, snapshot, offset_ms: Math.round(offsetMs) });
    if (error) throw new Error(error.message);
    const row = (Array.isArray(data) ? data[0] : data) as { started_at: string; server_now: string };
    return { startedAtMs: ms(row.started_at), serverNowMs: ms(row.server_now) };
  }
  async stop(teamId: string) {
    const { data, error } = await supabase!.rpc('stop_session', { t: teamId });
    if (error) throw new Error(error.message);
    return !!data;
  }
  async post(teamId: string, planId: string | null) {
    const { data, error } = await supabase!.rpc('post_plan', { t: teamId, pid: planId });
    if (error) throw new Error(error.code === 'P0001' && /not_saved/.test(error.message) ? 'not_saved' : error.message);
    const p = data as { plan_id: string; plan_name: string; posted_by: string | null; posted_by_name: string; posted_at: string; rev: string } | null;
    return p ? { ...p, posted_at_ms: ms(p.posted_at) } : null;
  }
}

class LocalBackend implements SessionBackend {
  private key(teamId: string, k: string) {
    return `session:${teamId}:${k}`;
  }
  async getState(teamId: string): Promise<SessionState> {
    const session = (await kvGet<SessionInfo>(this.key(teamId, 'live'))) ?? null;
    const posted = (await kvGet<PostedInfo>(this.key(teamId, 'posted'))) ?? null;
    return { session, posted, serverNowMs: Date.now() };
  }
  async start(teamId: string, snapshot: PlanData, offsetMs: number) {
    const startedAtMs = Date.now() - offsetMs;
    await kvSet(this.key(teamId, 'live'), { host_user_id: LOCAL_USER, plan_snapshot: snapshot, started_at_ms: startedAtMs } satisfies SessionInfo);
    return { startedAtMs, serverNowMs: Date.now() };
  }
  async stop(teamId: string) {
    const had = !!(await kvGet(this.key(teamId, 'live')));
    await kvSet(this.key(teamId, 'live'), null);
    return had;
  }
  async post(teamId: string, planId: string | null, extra?: { name: string }) {
    if (!planId) {
      await kvSet(this.key(teamId, 'posted'), null);
      return null;
    }
    const p: PostedInfo = { plan_id: planId, plan_name: extra?.name ?? '', posted_by: 'local', posted_by_name: 'You', posted_at_ms: Date.now(), rev: String(Date.now()) };
    await kvSet(this.key(teamId, 'posted'), p);
    return p;
  }
}

export const sessionBackend: SessionBackend = supabase ? new CloudBackend() : new LocalBackend();
export const IS_LOCAL_BACKEND = !supabase;
